import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { authMode, login, setMockSession } from "@/lib/auth";
import styles from "@/styles/login.module.css";

interface LoginSearch {
  redirect?: string;
  error?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: LoginPage,
});

// Roles offered by the mock login (dev only) — matches the API's RBAC roles.
const MOCK_ROLES = [
  "admin",
  "planning",
  "finance",
  "personeelszaken",
  "orderentry",
  "warehouse",
  "teamleider",
  "support",
  "directie",
  "billing",
  "backoffice",
  "qa",
] as const;

function LoginPage() {
  // The login page is always English, regardless of the user's locale preference
  // — and it says so (lang="en"), because <html lang> follows that preference
  // and is Dutch by default.
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("en");
  const { redirect, error } = Route.useSearch();
  const brandSub = t("login.taglineSub").trim();

  return (
    <div className={styles.shell} data-screen-label="Back-office Login" lang="en">
      <aside className={styles.brand}>
        <div className={styles.brandLogo}>
          <img src="/logo-white.png" alt={t("login.logoAlt")} />
        </div>

        <div className={styles.brandQuote}>
          <div className={styles.brandEyebrow}>{t("login.platform")}</div>
          <h1 className={styles.brandPayoff}>
            {/* Tagline uses a fixed split with <em> — kept as JSX (page is always English) */}
            Your business <em>on track.</em>
          </h1>
          {/* Reserved for a brand line under the payoff — rendered only when
              login.taglineSub is filled in, so an empty key leaves no gap. */}
          {brandSub && <p className={styles.brandSub}>{brandSub}</p>}
        </div>

        <div className={styles.brandFooter}>
          <div>{t("login.copyright")}</div>
          <div style={{ display: "flex", gap: "14px" }}>
            <a href="#">{t("login.privacy")}</a>
            <a href="#">{t("login.support")}</a>
          </div>
        </div>
      </aside>

      <main className={styles.form}>
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardEyebrow}>{t("login.signInEyebrow")}</div>
            <h2 className={styles.cardTitle}>{t("login.welcomeBack")}</h2>
            <p className={styles.cardLede}>{t("login.signInDesc")}</p>
          </div>

          {error && (
            <div className={styles.errorBanner} role="alert">
              {t("login.signInFailed")}
            </div>
          )}

          {/* Microsoft Entra SSO */}
          <button
            type="button"
            className={styles.ssoBtn}
            onClick={() => login()}
          >
            <span className={styles.ssoBtnIcon}>
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
                <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
                <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
                <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
              </svg>
            </span>
            <span>
              {t("login.signInWith")}
              <span className={styles.ssoBtnMeta}>{t("login.willemsInternal")}</span>
            </span>
            <svg
              className={styles.ssoBtnArrow}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>

          <div className={styles.secPill}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 2 4 5v7c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V5l-8-3Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            {t("login.mfaEnforced")}
          </div>

          {authMode() === "mock" && <MockLogin redirect={redirect} />}
        </div>
      </main>
    </div>
  );
}

function MockLogin({ redirect }: { redirect?: string }) {
  // Always English — matches the rest of the login page.
  const { i18n } = useTranslation();
  const t = i18n.getFixedT("en");
  const [email, setEmail] = useState("dev@willems.local");
  const [roles, setRoles] = useState<string[]>(["admin"]);

  function toggleRole(role: string) {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || roles.length === 0) return;
    setMockSession({ email, roles });
    // Full navigation so the route guard re-runs fetchSession cleanly.
    window.location.href = redirect ?? "/dashboard";
  }

  return (
    <div className={styles.mockBanner}>
      <p>{t("login.devMode")}</p>
      <form onSubmit={submit}>
        <input
          className={styles.mockInput}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="dev@willems.local"
          required
        />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px 14px",
            margin: "10px 0",
          }}
        >
          {MOCK_ROLES.map((role) => (
            <label
              key={role}
              className="t-body-sm"
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={() => toggleRole(role)}
              />
              {role}
            </label>
          ))}
        </div>
        <button type="submit" className={styles.mockBtn}>
          {t("login.signInAsMock")}
        </button>
      </form>
    </div>
  );
}
